// Copyright (c) 2026 Michael F. Kamprath
//
// orientation-prompt.js -- landscape orientation/aspect-ratio prompt.
//
// On mobile portrait: "Please rotate your device to landscape"
// On desktop with a too-narrow window: "Please widen your browser window"
//
// The prompt appears whenever the viewport aspect ratio drops below a
// minimum threshold (roughly 1:1).  Device type detection uses the
// `hover` and `pointer` media queries which are more reliable than
// user-agent sniffing.
//
// Localized via the standard t() resolver.  The prompt re-reads the
// active locale on every update() (resize / settings change), so
// switching locales while the prompt is visible refreshes the
// message in-place.

import { getLocale, onChange as onSettingsChange } from '../settings.js';
import { t } from './strings/index.js';

/**
 * Returns true when the primary input device is a touchscreen with
 * no hover ability -- i.e. a phone or tablet, not a desktop/laptop.
 */
function isTouchDevice() {
  return (
    matchMedia('(hover: none) and (pointer: coarse)').matches
  );
}

/** Minimum viewport aspect ratio before the prompt appears. */
const MIN_RATIO = 1.0;

/**
 * Create the orientation prompt and attach it to the parent.
 *
 * @param {HTMLElement} parent
 * @returns {{ element: HTMLElement, destroy: () => void }}
 */
export function createOrientationPrompt(parent) {
  const el = document.createElement('div');
  el.className = 'orientation-prompt';
  el.setAttribute('aria-live', 'polite');

  const icon = document.createElement('div');
  icon.className = 'orientation-prompt__icon';
  icon.setAttribute('aria-hidden', 'true');
  el.appendChild(icon);

  const msg = document.createElement('p');
  msg.className = 'orientation-prompt__message';
  el.appendChild(msg);

  parent.appendChild(el);

  function update() {
    const ratio = window.innerWidth / window.innerHeight;
    const tooNarrow = ratio < MIN_RATIO;

    el.classList.toggle('orientation-prompt--visible', tooNarrow);

    if (tooNarrow) {
      const mobile = isTouchDevice();
      icon.textContent = mobile ? '\u{1F4F1}' : '\u{1F5A5}'; // phone or desktop emoji
      const locale = getLocale();
      msg.textContent = mobile
        ? t('orientationPrompt.rotateToLandscape', locale)
        : t('orientationPrompt.widenBrowser', locale);
      // Mirror the locale onto the message element so screen readers
      // pick the correct pronunciation rules (especially relevant for
      // CJK locales where SR voices are language-specific).
      msg.lang = locale;
    }
  }

  update();
  window.addEventListener('resize', update);
  // Re-render when the user changes locale via the settings panel.
  // Cheap (string lookup + textContent set); covers the rare-but-real
  // case where someone changes language while the prompt is showing.
  const unsubscribe = onSettingsChange(() => { update(); });

  return {
    element: el,
    destroy() {
      window.removeEventListener('resize', update);
      if (typeof unsubscribe === 'function') unsubscribe();
    },
  };
}
